import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Chip, Collapse, Stack, Typography } from '@mui/material';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import { memo, useState } from 'react';
import { GLASS_BLUR, SUPPORTS_BACKDROP_FILTER } from 'src/constants/glass';
import { findSmsSessionParticipation } from 'src/pages/Conversations/Chat/InputField/utils';
import { useChatCommonData } from 'src/pages/Conversations/Chat/registry/common-data-context';
import { CONVERSATION_CARD_BORDER_RADIUS } from 'src/pages/Conversations/constants';
import { useConfirmationModal } from 'src/utils/hooks/use-confirmation-modal';
import { useScreenDimensions } from 'src/utils/system';
import { SmsLiveDot } from './sms-live-dot';
import { useSmsSessionContext } from './sms-session-context';
import { type SmsSessionGroup } from './sms-session-grouping';
import { SmsSessionThread } from './sms-session-thread';

function maskPhoneNumber(raw: string | null | undefined): string | null {
  const digits = (raw ?? '').replace(/\D/g, '');
  if (digits.length < 7) return null;
  const countryCode = digits.length > 10 ? digits.slice(0, digits.length - 10) : '1';
  return `+${countryCode} ··· ${digits.slice(-4)}`;
}

const panelBoxSx: SxProps<Theme> = {
  mx: 1,
  mb: 0.75,
  borderRadius: CONVERSATION_CARD_BORDER_RADIUS,
  border: (theme) => `1px solid ${alpha(theme.palette.custom.OV.entity.conversation, 0.6)}`,
  backgroundColor: (theme) => theme.palette.background.paper,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  flexShrink: 1,
};

// Floats over the thread, so it takes the composer's glass — bare translucency let the
// messages behind it read straight through.
const miniBarSx: SxProps<Theme> = {
  px: 1.5,
  py: 0.5,
  borderTop: (theme) => `1px solid ${theme.palette.divider}`,
  cursor: 'pointer',
  backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.72),
  backdropFilter: GLASS_BLUR,
  WebkitBackdropFilter: GLASS_BLUR,
  [SUPPORTS_BACKDROP_FILTER]: {
    backgroundColor: (theme) => theme.palette.background.paper,
  },
};

const MOBILE_THREAD_MAX_HEIGHT = '8rem';

const collapseSx: SxProps<Theme> = {
  flexShrink: 1,
  minHeight: 0,
  '& .MuiCollapse-wrapper': { minHeight: 0 },
  '& .MuiCollapse-wrapperInner': { display: 'flex', flexDirection: 'column', minHeight: 0 },
};

const panelHeaderSx: SxProps<Theme> = {
  px: 1.5,
  py: 1,
  borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
};

const endSessionButtonSx: SxProps<Theme> = {
  ml: 'auto',
  px: 1.25,
  py: 0.25,
  minWidth: 0,
  flexShrink: 0,
  borderRadius: 100,
  fontSize: '0.6875rem',
  fontWeight: 700,
  letterSpacing: '0.04em',
  color: 'error.main',
  backgroundColor: (theme) => alpha(theme.palette.error.main, 0.12),
  '&:hover': {
    backgroundColor: (theme) => alpha(theme.palette.error.main, 0.2),
  },
};

const SmsSessionPanelFull = ({
  group,
  onEnd,
  isEnding,
  threadMaxHeight,
}: {
  group: SmsSessionGroup;
  onEnd: () => void;
  isEnding: boolean;
  threadMaxHeight: string;
}) => {
  const requesterFirstName = useSmsSessionContext((v) => v.requesterFirstName);
  const openSessionChannelId = useSmsSessionContext((v) => v.openSessionChannelId);
  const conversationParticipations = useChatCommonData((s) => s.conversationParticipations);
  const clientPhone = maskPhoneNumber(
    findSmsSessionParticipation(conversationParticipations, openSessionChannelId)?.channel
      ?.externalIdentifier,
  );

  const headerMeta = [
    requesterFirstName ? `texting ${requesterFirstName}` : null,
    clientPhone,
  ].filter(Boolean);

  return (
    <Box sx={panelBoxSx}>
      <Stack direction="row" alignItems="center" gap={0.75} sx={panelHeaderSx}>
        <SmsLiveDot />
        <Typography variant="body2" fontWeight={600}>
          SMS session
        </Typography>
        {headerMeta.length > 0 ? (
          <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
            · {headerMeta.join(' · ')}
          </Typography>
        ) : null}
        <Button size="small" onClick={onEnd} disabled={isEnding} sx={endSessionButtonSx}>
          {isEnding ? 'ENDING…' : 'END SESSION'}
        </Button>
      </Stack>

      <SmsSessionThread group={group} maxHeight={threadMaxHeight} pinToNewest />
    </Box>
  );
};

const SmsSessionMiniBar = ({
  messageCount,
  onExpand,
}: {
  messageCount: number;
  onExpand: () => void;
}) => (
  <Stack direction="row" alignItems="center" onClick={onExpand} sx={miniBarSx}>
    <Chip
      label="LIVE"
      size="small"
      color="success"
      sx={{ height: '1.1rem', fontSize: '0.625rem', fontWeight: 700, mr: 1 }}
    />
    <Typography variant="caption" sx={{ flex: 1 }}>
      SMS session · {messageCount} message{messageCount !== 1 ? 's' : ''}
    </Typography>
    <ExpandMoreIcon sx={{ fontSize: '1.1rem', color: 'text.secondary' }} />
  </Stack>
);

export const SmsSessionPanel = memo(function SmsSessionPanel() {
  const { isMobile } = useScreenDimensions();
  const [mobileExpanded, setMobileExpanded] = useState(false);

  const openSessionId = useSmsSessionContext((v) => v.openSessionId);
  const sessionGroups = useSmsSessionContext((v) => v.sessionGroups);
  const isEnding = useSmsSessionContext((v) => v.isEnding);
  const endSession = useSmsSessionContext((v) => v.endSession);

  const { confirm } = useConfirmationModal();

  const openGroup = sessionGroups.find((g) => g.sessionId === openSessionId) ?? null;

  if (!openGroup) return null;

  const handleEnd = async () => {
    const confirmed = await confirm({
      title: 'End SMS session?',
      body: 'End this SMS session? The transcript stays on the timeline; a new text from the client will start a new session.',
    });
    if (confirmed) {
      await endSession();
    }
  };

  if (isMobile) {
    return (
      <>
        <SmsSessionMiniBar
          messageCount={openGroup.messageCount}
          onExpand={() => setMobileExpanded((v) => !v)}
        />
        <Collapse
          in={mobileExpanded}
          data-sms-session-expanded={mobileExpanded ? '' : undefined}
          sx={collapseSx}
        >
          <SmsSessionPanelFull
            group={openGroup}
            onEnd={() => void handleEnd()}
            isEnding={isEnding}
            threadMaxHeight={MOBILE_THREAD_MAX_HEIGHT}
          />
        </Collapse>
      </>
    );
  }

  return (
    <SmsSessionPanelFull
      group={openGroup}
      onEnd={() => void handleEnd()}
      isEnding={isEnding}
      threadMaxHeight="12rem"
    />
  );
});
